import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  LinearProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  FormControlLabel,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Print as PrintIcon,
  Email as EmailIcon,
  ArrowBack as BackIcon,
  CheckCircle as FinalizeIcon,
  Replay as RevertIcon,
  ShoppingCart as ShoppingCartIcon,
  Code as XmlIcon,
  ContentCopy as CopyIcon,
  Receipt as InvoiceIcon,
  RemoveCircle as CreditNoteIcon,
  AddCircle as DebitNoteIcon,
  Description as ProformaIcon,
  History as HistoryIcon,
  Assignment as ProtocolIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';

import { GET_DOCUMENT_BY_ID, EXPORT_DOCUMENT_AS_UBL, GENERATE_DOCUMENT_PDF, GENERATE_DOCUMENT_ODT, GENERATE_DOCUMENT_XLSX, GENERATE_DOCUMENT_ODS, GET_AUDIT_LOGS_BY_DOCUMENT } from '../graphql/queries';
import { UPDATE_DOCUMENT_STATUS, CANCEL_DOCUMENT, REVERT_TO_DRAFT, SEND_DOCUMENT_BY_EMAIL, COPY_DOCUMENT } from '../graphql/mutations';
import { DocumentType, DocumentStatus } from '../types';

const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [copyMenuAnchor, setCopyMenuAnchor] = useState(null);

  const { data, loading, error, refetch } = useQuery(GET_DOCUMENT_BY_ID, {
    variables: { id },
    skip: !id,
  });

  const { data: auditData, loading: auditLoading, refetch: refetchAudit } = useQuery(GET_AUDIT_LOGS_BY_DOCUMENT, {
    variables: { documentId: id },
    skip: !id,
  });

  const [generatePdf, { loading: pdfLoading }] = useLazyQuery(GENERATE_DOCUMENT_PDF, {
    fetchPolicy: 'network-only'
  });

  const [generateOdt, { loading: odtLoading }] = useLazyQuery(GENERATE_DOCUMENT_ODT, {
    fetchPolicy: 'network-only'
  });

  const [generateXlsx, { loading: xlsxLoading }] = useLazyQuery(GENERATE_DOCUMENT_XLSX, {
    fetchPolicy: 'network-only'
  });

  const [generateOds, { loading: odsLoading }] = useLazyQuery(GENERATE_DOCUMENT_ODS, {
    fetchPolicy: 'network-only'
  });

  const [
    updateDocumentStatus,
    { loading: updateLoading, error: updateError },
  ] = useMutation(UPDATE_DOCUMENT_STATUS, {
    onCompleted: () => {
      refetch();
      refetchAudit();
    },
    onError: (error) => {
      console.error('Error updating document status:', error);
    },
  });

  const [cancelDocument, { loading: cancelLoading }] = useMutation(CANCEL_DOCUMENT, {
    onCompleted: () => {
      refetch();
      refetchAudit();
    },
    onError: (error) => {
      console.error('Error cancelling document:', error);
      alert('Грешка при анулиране: ' + error.message);
    },
  });

  const [revertToDraft, { loading: revertLoading }] = useMutation(REVERT_TO_DRAFT, {
    onCompleted: () => {
      refetch();
      refetchAudit();
    },
    onError: (error) => {
      console.error('Error reverting document to draft:', error);
      alert('Грешка при връщане в чернова: ' + error.message);
    },
  });

  const [sendDocumentByEmail] = useMutation(SEND_DOCUMENT_BY_EMAIL);

  const [copyDocument, { loading: copyLoading }] = useMutation(COPY_DOCUMENT, {
    onCompleted: (data) => {
      if (data?.copyDocument?.id) {
        setSnackbar({ open: true, message: 'Документът е копиран успешно', severity: 'success' });
        navigate(`/documents/${data.copyDocument.id}`);
      }
    },
    onError: (error) => {
      console.error('Error copying document:', error);
      setSnackbar({ open: true, message: 'Грешка при копиране: ' + error.message, severity: 'error' });
    },
  });

  const [exportUbl, { loading: ublExporting }] = useLazyQuery(EXPORT_DOCUMENT_AS_UBL, {
    onCompleted: (result) => {
      if (result?.exportDocumentAsUbl?.success) {
        const base64Xml = result.exportDocumentAsUbl.xml;
        const decodedXml = decodeURIComponent(escape(window.atob(base64Xml)));
        
        const filename = result.exportDocumentAsUbl.filename;
        const blob = new Blob([decodedXml], { type: 'application/xml' });
        const url = window.URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = filename;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setSnackbar({ open: true, message: 'UBL XML експортиран успешно', severity: 'success' });
      } else {
        setSnackbar({
          open: true,
          message: result?.exportDocumentAsUbl?.message || 'Грешка при експорт',
          severity: 'error'
        });
      }
    },
    onError: (error) => {
      setSnackbar({ open: true, message: 'Грешка при експорт: ' + error.message, severity: 'error' });
    }
  });

  const handlePrint = async (isCopy, printMode = null) => {
    try {
      const variables = { documentId: id, isCopy };
      if (printMode) {
        variables.printMode = printMode;
      }

      const { data: pdfData } = await generatePdf({ variables });

      if (pdfData?.generateDocumentPdf?.success) {
        const { pdfBase64, filename } = pdfData.generateDocumentPdf;
        const link = window.document.createElement('a');
        link.href = `data:application/pdf;base64,${pdfBase64}`;
        link.download = filename;
        link.click();
      } else {
        setSnackbar({
          open: true,
          message: pdfData?.generateDocumentPdf?.errorMessage || 'Грешка при генериране на PDF',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      setSnackbar({ open: true, message: 'Грешка при генериране: ' + error.message, severity: 'error' });
    }
  };

  const handleDownloadOdt = async () => {
    try {
      const { data: odtData } = await generateOdt({ variables: { documentId: id } });

      if (odtData?.generateDocumentOdt?.success) {
        const { pdfBase64, filename } = odtData.generateDocumentOdt;
        const link = window.document.createElement('a');
        link.href = `data:application/vnd.oasis.opendocument.text;base64,${pdfBase64}`;
        link.download = filename;
        link.click();
      } else {
        setSnackbar({
          open: true,
          message: odtData?.generateDocumentOdt?.errorMessage || 'Грешка при генериране на ODT',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error generating ODT:', error);
      setSnackbar({ open: true, message: 'Грешка при генериране: ' + error.message, severity: 'error' });
    }
  };

  const handleDownloadXlsx = async () => {
    try {
      const { data: xlsxData } = await generateXlsx({ variables: { documentId: id } });

      if (xlsxData?.generateDocumentXlsx?.success) {
        const { pdfBase64, filename } = xlsxData.generateDocumentXlsx;
        const link = window.document.createElement('a');
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${pdfBase64}`;
        link.download = filename;
        link.click();
      } else {
        setSnackbar({
          open: true,
          message: xlsxData?.generateDocumentXlsx?.errorMessage || 'Грешка при генериране на XLSX',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error generating XLSX:', error);
      setSnackbar({ open: true, message: 'Грешка при генериране: ' + error.message, severity: 'error' });
    }
  };

  const handleDownloadOds = async () => {
    try {
      const { data: odsData } = await generateOds({ variables: { documentId: id } });

      if (odsData?.generateDocumentOds?.success) {
        const { pdfBase64, filename } = odsData.generateDocumentOds;
        const link = window.document.createElement('a');
        link.href = `data:application/vnd.oasis.opendocument.spreadsheet;base64,${pdfBase64}`;
        link.download = filename;
        link.click();
      } else {
        setSnackbar({
          open: true,
          message: odsData?.generateDocumentOds?.errorMessage || 'Грешка при генериране на ODS',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error generating ODS:', error);
      setSnackbar({ open: true, message: 'Грешка при генериране: ' + error.message, severity: 'error' });
    }
  };

  const handleStatusChange = () => {
    const currentStatus = document.status;
    const newStatus =
      currentStatus === DocumentStatus.DRAFT
        ? DocumentStatus.FINAL
        : DocumentStatus.DRAFT;

    updateDocumentStatus({
      variables: {
        documentId: id,
        status: newStatus,
      },
    });
  };

  const handleCancelDocument = () => {
    const reason = window.prompt('Въведете причина за анулиране (незадължително):');
    if (reason !== null) {
      cancelDocument({
        variables: {
          documentId: id,
          reason: reason || null,
        },
      });
    }
  };

  const handleRevertToDraft = () => {
    if (window.confirm('Сигурни ли сте, че искате да върнете документа в чернова?')) {
      revertToDraft({
        variables: {
          documentId: id,
        },
      });
    }
  };

  const handleOpenEmailDialog = () => {
    if (data?.documentById?.client?.email) {
      setRecipientEmail(data.documentById.client.email);
    }
    setEmailDialogOpen(true);
  };

  const handleExportUbl = () => {
    exportUbl({ variables: { documentId: id } });
  };

  const handleCopyMenuOpen = (event) => {
    setCopyMenuAnchor(event.currentTarget);
  };

  const handleCopyMenuClose = () => {
    setCopyMenuAnchor(null);
  };

  const handleCopyDocument = (targetType) => {
    handleCopyMenuClose();
    copyDocument({
      variables: {
        input: {
          sourceDocumentId: id,
          targetDocumentType: targetType,
        },
      },
    });
  };

  const getCopyMenuItems = () => {
    const currentType = data?.documentById?.documentType;
    const items = [];

    if (currentType !== DocumentType.INVOICE) {
      items.push({
        type: DocumentType.INVOICE,
        label: 'Копирай във Фактура',
        icon: <InvoiceIcon />,
      });
    }

    if (currentType === DocumentType.INVOICE || currentType === DocumentType.DEBIT_NOTE) {
      items.push({
        type: DocumentType.CREDIT_NOTE,
        label: 'Копирай в Кредитно известие',
        icon: <CreditNoteIcon color="error" />,
        hint: 'Количествата ще бъдат с минус',
      });
    }

    if (currentType === DocumentType.INVOICE) {
      items.push({
        type: DocumentType.DEBIT_NOTE,
        label: 'Копирай в Дебитно известие',
        icon: <DebitNoteIcon color="success" />,
      });
    }

    if (currentType !== DocumentType.PROFORMA) {
      items.push({
        type: DocumentType.PROFORMA,
        label: 'Копирай в Проформа',
        icon: <ProformaIcon />,
      });
    }

    items.push({
      type: currentType,
      label: 'Дублирай документа',
      icon: <CopyIcon />,
    });

    return items;
  };

  const handleSendEmail = async () => {
    if (!recipientEmail || !recipientEmail.includes('@')) {
      setSnackbar({ open: true, message: 'Моля, въведете валиден имейл адрес', severity: 'error' });
      return;
    }

    setEmailSending(true);

    try {
      // 1. Generate PDF (Server-side)
      const { data: pdfData } = await generatePdf({ variables: { documentId: id } });
      
      if (!pdfData?.generateDocumentPdf?.success) {
        throw new Error(pdfData?.generateDocumentPdf?.errorMessage || 'Грешка при генериране на PDF');
      }

      const pdfBase64 = pdfData.generateDocumentPdf.pdfBase64;

      // 2. Send Email with PDF (UBL XML is available on validation page)
      const { data: emailResult } = await sendDocumentByEmail({
        variables: {
          input: {
            documentId: id,
            recipientEmail: recipientEmail,
            pdfBase64: pdfBase64,
            includeUblXml: false,
          },
        },
      });

      if (emailResult?.sendDocumentByEmail?.success) {
        setSnackbar({ open: true, message: emailResult.sendDocumentByEmail.message, severity: 'success' });
        setEmailDialogOpen(false);
        setRecipientEmail('');
      } else {
        setSnackbar({
          open: true,
          message: emailResult?.sendDocumentByEmail?.message || 'Грешка при изпращане',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSnackbar({ open: true, message: 'Грешка при изпращане: ' + error.message, severity: 'error' });
    } finally {
      setEmailSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Грешка при зареждане на документа: {error.message}
      </Alert>
    );
  }

  if (!data?.documentById) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Документът не е намерен
      </Alert>
    );
  }

  const document = data.documentById;
  const documentItems = document.documentItems || [];
  
  // Calculate VAT breakdown by rate
  const vatBreakdown = documentItems.reduce((acc, item) => {
    const rate = item.vatRate;
    if (!acc[rate]) {
      acc[rate] = { rate, base: 0, amount: 0 };
    }
    acc[rate].base += item.lineTotal;
    acc[rate].amount += item.vatAmount;
    return acc;
  }, {});
  
  const totalVatAmount = documentItems.reduce((acc, item) => acc + (item.vatAmount || 0), 0);
  const subTotalAmount = document.subtotalAmount || (document.totalAmountWithVat - totalVatAmount);

  const getStatusColor = (status) => {
    switch (status) {
      case DocumentStatus.DRAFT:
        return 'warning';
      case DocumentStatus.FINAL:
        return 'success';
      case DocumentStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case DocumentStatus.DRAFT:
        return 'Чернова';
      case DocumentStatus.FINAL:
        return 'Приключен';
      case DocumentStatus.CANCELLED:
        return 'Анулиран';
      default:
        return status;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case DocumentType.INVOICE:
        return 'Фактура';
      case DocumentType.CREDIT_NOTE:
        return 'Кредитно известие';
      case DocumentType.DEBIT_NOTE:
        return 'Дебитно известие';
      case DocumentType.PROFORMA:
        return 'Проформа фактура';
      default:
        return type;
    }
  };

  const InfoRow = ({
    label,
    value,
  }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
      <Typography variant="body2" color="text.secondary" component="div">
        {label}:
      </Typography>
      <Typography variant="body2" fontWeight="medium" component="div">
        {value}
      </Typography>
    </Box>
  );

  const isDraft = document.status === DocumentStatus.DRAFT;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/documents')}
          sx={{ mr: 2 }}
        >
          Назад
        </Button>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {getTypeLabel(document.documentType)} {document.fullDocumentNumber}
        </Typography>
        <Chip
          label={getStatusLabel(document.status)}
          color={getStatusColor(document.status)}
          size="small"
        />
      </Box>

      {updateError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Грешка при промяна на статуса: {updateError.message}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Действия */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Действия
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
              {document.status !== DocumentStatus.CANCELLED && (
                <Button
                  variant="contained"
                  color={isDraft ? 'success' : 'warning'}
                  startIcon={isDraft ? <FinalizeIcon /> : <RevertIcon />}
                  onClick={handleStatusChange}
                  disabled={updateLoading || pdfLoading}
                >
                  {isDraft ? 'Финализирай' : 'Върни в чернова'}
                </Button>
              )}
              {document.status === DocumentStatus.FINAL && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleCancelDocument}
                  disabled={cancelLoading || pdfLoading}
                >
                  Анулирай
                </Button>
              )}
              {document.status === DocumentStatus.CANCELLED && (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<RevertIcon />}
                  onClick={handleRevertToDraft}
                  disabled={revertLoading || pdfLoading}
                >
                  Върни в чернова
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => handlePrint(false)}
                disabled={isDraft || pdfLoading}
              >
                {pdfLoading ? 'Генериране...' : 'Принтирай'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => handlePrint(true)}
                disabled={isDraft || pdfLoading}
              >
                {pdfLoading ? 'Генериране...' : 'Принтирай Копие'}
              </Button>
              {/* Протоколи */}
              {document.documentType === DocumentType.INVOICE && (
                <Button
                  variant="outlined"
                  startIcon={<ProtocolIcon />}
                  onClick={() => handlePrint(false, 'PROTOCOL')}
                  disabled={isDraft || pdfLoading}
                >
                  Протокол
                </Button>
              )}
              {document.documentType === DocumentType.CREDIT_NOTE && (
                <Button
                  variant="outlined"
                  startIcon={<ProtocolIcon />}
                  onClick={() => handlePrint(false, 'RETURN_PROTOCOL')}
                  disabled={isDraft || pdfLoading}
                >
                  Протокол Връщане
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={handleDownloadOdt}
                disabled={isDraft || odtLoading}
              >
                {odtLoading ? 'Генериране...' : 'ODT'}
              </Button>

              <Button
                variant="outlined"
                onClick={handleDownloadXlsx}
                disabled={isDraft || xlsxLoading}
              >
                {xlsxLoading ? 'Генериране...' : 'XLSX'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleDownloadOds}
                disabled={isDraft || odsLoading}
              >
                {odsLoading ? 'Генериране...' : 'ODS'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={handleOpenEmailDialog}
                disabled={isDraft || emailSending}
              >
                Изпрати по имейл
              </Button>
              <Button
                variant="outlined"
                startIcon={<XmlIcon />}
                onClick={handleExportUbl}
                disabled={isDraft || ublExporting}
                title="Експорт като UBL 2.1 XML (EN 16931 / Peppol)"
              >
                {ublExporting ? 'Експортиране...' : 'UBL XML'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={handleCopyMenuOpen}
                disabled={copyLoading || document.status === DocumentStatus.CANCELLED}
                title="Копирай в друг тип документ"
              >
                {copyLoading ? 'Копиране...' : 'Копирай'}
              </Button>
              <Menu
                anchorEl={copyMenuAnchor}
                open={Boolean(copyMenuAnchor)}
                onClose={handleCopyMenuClose}
              >
                {getCopyMenuItems().map((item) => (
                  <MenuItem
                    key={item.type}
                    onClick={() => handleCopyDocument(item.type)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      secondary={item.hint}
                    />
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
            {document.status === DocumentStatus.CANCELLED && document.cancellationReason && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <strong>Причина за анулиране:</strong> {document.cancellationReason}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Основна информация */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Основна информация
            </Typography>
            <InfoRow label="Номер" value={document.fullDocumentNumber} />
            <InfoRow label="Тип документ" value={getTypeLabel(document.documentType)} />
            <InfoRow label="Статус" value={
              <Chip
                label={getStatusLabel(document.status)}
                color={getStatusColor(document.status)}
                size="small"
              />
            } />
            <InfoRow 
              label="Дата на издаване" 
              value={new Date(document.issueDate).toLocaleDateString('bg-BG')} 
            />
            {document.vatDate && (
              <InfoRow 
                label="VAT дата" 
                value={new Date(document.vatDate).toLocaleDateString('bg-BG')} 
              />
            )}
            <InfoRow 
              label="Срок за плащане" 
              value={new Date(document.dueDate).toLocaleDateString('bg-BG')} 
            />
          </CardContent>
        </Card>

        {/* Информация за клиента */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Клиент
            </Typography>
            <InfoRow label="Име" value={document.client.name} />
            {document.client.address && (
              <InfoRow label="Адрес" value={document.client.address} />
            )}
            {document.client.vatNumber && (
              <InfoRow label="ДДС номер" value={document.client.vatNumber} />
            )}
          </CardContent>
        </Card>

        {/* Артикули */}
        {documentItems && documentItems.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartIcon color="primary" />
                Артикули
              </Typography>
              <TableContainer component={Paper} elevation={2}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.light' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Описание</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Количество</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ед. цена</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>ДДС %</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Сума</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documentItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2">{item.itemDescription || item.effectiveItemName}</Typography>
                          {item.vatRate === 0 && item.vatExemptionReason && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                              Основание: {item.vatExemptionReason.reasonName} ({item.vatExemptionReason.legalBasis})
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{item.unitPrice.toFixed(2)}</TableCell>
                        <TableCell align="right">{item.vatRate.toFixed(2)}%</TableCell>
                        <TableCell align="right">{item.lineTotal.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
        </Card>
        )}

        {/* Totals */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Общо
            </Typography>
            <InfoRow 
              label="Междинна сума" 
              value={`${subTotalAmount.toFixed(2)} ${document.currencyCode}`} 
            />
            
            {/* VAT Breakdown */}
            {Object.values(vatBreakdown).sort((a, b) => b.rate - a.rate).map((vat) => (
              <InfoRow 
                key={vat.rate}
                label={`ДДС (${vat.rate.toFixed(2)}%)`} 
                value={`${vat.amount.toFixed(2)} ${document.currencyCode}`} 
              />
            ))}
            
            {/* If no items or breakdown failed but totalVat exists */}
            {Object.keys(vatBreakdown).length === 0 && totalVatAmount > 0 && (
               <InfoRow 
                label="ДДС" 
                value={`${totalVatAmount.toFixed(2)} ${document.currencyCode}`} 
              />
            )}

            <Divider sx={{ my: 1 }} />
            <InfoRow 
              label={<Typography fontWeight="bold">Обща сума</Typography>} 
              value={<Typography fontWeight="bold">{`${document.totalAmountWithVat.toFixed(2)} ${document.currencyCode}`}</Typography>} 
            />
          </CardContent>
        </Card>
        
        {/* Информация за фирмата */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Фирма
            </Typography>
            <InfoRow label="Име" value={document.company.name} />
            {document.company.address && (
              <InfoRow label="Адрес" value={document.company.address} />
            )}
            {document.company.vatNumber && (
              <InfoRow label="ДДС номер" value={document.company.vatNumber} />
            )}
          </CardContent>
        </Card>

        {/* История на промените (Audit Log) */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon color="primary" />
              История на промените
            </Typography>
            {auditLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : auditData?.auditLogsByDocument?.length > 0 ? (
              <TableContainer component={Paper} elevation={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Дата и час</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Потребител</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Действие</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>IP адрес</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditData.auditLogsByDocument.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          {new Date(log.timestamp).toLocaleString('bg-BG', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </TableCell>
                        <TableCell>{log.username || '-'}</TableCell>
                        <TableCell>{log.description}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {log.ipAddress || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Няма записи в историята
              </Typography>
            )}
          </CardContent>
        </Card>

      </Stack>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => !emailSending && setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Изпрати документ по имейл</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Имейл адрес на получателя"
            type="email"
            fullWidth
            variant="outlined"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            disabled={emailSending}
            sx={{ mt: 1 }}
          />
          {emailSending && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 2 }}>
              <CircularProgress size={24} />
              <Typography>Генериране на PDF и изпращане...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={emailSending}>
            Отказ
          </Button>
          <Button onClick={handleSendEmail} variant="contained" disabled={emailSending}>
            {emailSending ? 'Изпрати' : 'Изпрати'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentDetail;
